import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  ArrowLeft,
  ClipboardList,
  FileSearch,
  Loader2,
  LogIn,
  LogOut,
  MessagesSquare,
  ShieldCheck,
  UserPlus,
} from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';
import { z } from 'zod';

import { ErroApi } from '@/nucleo/auth/cliente-api';
import { validarCpf } from '@/nucleo/validarDocumento';
import { Alert, AlertDescription, AlertTitle } from '@/ui/base/alert';
import { Badge } from '@/ui/base/badge';
import { Button } from '@/ui/base/button';
import { Card, CardContent, CardDescription, CardHeader } from '@/ui/base/card';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/ui/base/form';
import { Input } from '@/ui/base/input';
import { Skeleton } from '@/ui/base/skeleton';

import { mascaraCpf, somenteDigitos } from '../contrato';
import {
  apiDoInscrito,
  CHAVE_DA_SESSAO_DO_INSCRITO,
  sairDoInscrito,
  useSessaoDoInscrito,
} from './api';
import { CampoDeSenha } from './CampoDeSenha';

/**
 * Entrada do inscrito no portal público (`/credenciados/entrar`): CPF e senha.
 *
 * **Há campo de senha, por decisão do dono (D-10, CLAUDE.md, 2026-09-28).** A
 * conta do inscrito vive fora do Keycloak e só serve para acompanhar a
 * inscrição — não abre o Portal do Credenciado (RN-CP-14). A sessão é um
 * cookie que morre quando o navegador fecha (D-11).
 *
 * A recusa é sempre a mesma frase, com CPF sem conta ou com senha errada: a
 * tela não serve para descobrir quem está cadastrado.
 */

/** Para onde vai quem entra: a lista das próprias inscrições (F06). */
export const DESTINO_DEPOIS_DE_ENTRAR = '/credenciados/acompanhamento';

const esquemaDeEntrada = z.object({
  cpf: z
    .string()
    .trim()
    .superRefine((valor, ctx) => {
      const digitos = somenteDigitos(valor);
      if (digitos.length === 0) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Informe o seu CPF' });
      } else if (digitos.length !== 11) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'O CPF tem 11 dígitos' });
      } else if (!validarCpf(digitos)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'CPF inválido — confira os números digitados',
        });
      }
    }),
  senha: z.string().min(1, 'Informe a sua senha').max(256),
});

type ValoresDaEntrada = z.infer<typeof esquemaDeEntrada>;

const VANTAGENS_DE_ENTRAR = [
  { icone: ClipboardList, texto: 'Acompanhar o status e o protocolo de cada veículo inscrito' },
  { icone: FileSearch, texto: 'Continuar uma inscrição que ficou pela metade' },
  { icone: MessagesSquare, texto: 'Ver o próximo passo e o prazo de cada etapa' },
] as const;

export function EntrarPage() {
  const navegar = useNavigate();
  const cliente = useQueryClient();
  const sessao = useSessaoDoInscrito();
  const [recusa, setRecusa] = useState<string | null>(null);

  const formulario = useForm<ValoresDaEntrada>({
    resolver: zodResolver(esquemaDeEntrada),
    defaultValues: { cpf: '', senha: '' },
    mode: 'onSubmit',
  });

  async function entrar(valores: ValoresDaEntrada) {
    setRecusa(null);
    try {
      await apiDoInscrito.entrar(somenteDigitos(valores.cpf), valores.senha);
      await cliente.invalidateQueries({ queryKey: CHAVE_DA_SESSAO_DO_INSCRITO });
      navegar(DESTINO_DEPOIS_DE_ENTRAR);
    } catch (erro) {
      formulario.setValue('senha', '');
      setRecusa(
        erro instanceof ErroApi
          ? erro.aviso
          : 'Não foi possível entrar. Tente de novo em instantes.',
      );
    }
  }

  async function sair() {
    await sairDoInscrito().catch(() => undefined);
    await cliente.invalidateQueries({ queryKey: CHAVE_DA_SESSAO_DO_INSCRITO });
  }

  return (
    <div className="mx-auto w-full max-w-5xl px-4 py-8 sm:px-6 sm:py-12">
      <Link
        to="/credenciados"
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground underline-offset-4 transition-colors hover:text-foreground hover:underline"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden="true" />
        Voltar para a área de credenciados
      </Link>

      <div className="mt-6 grid gap-8 lg:grid-cols-[1fr_minmax(0,440px)] lg:items-start lg:gap-12">
        <section className="space-y-6" aria-labelledby="titulo-da-entrada">
          <div className="space-y-2">
            <Badge variant="outline" className="gap-1.5 border-primary/30 bg-primary/5">
              <LogIn className="h-3.5 w-3.5" aria-hidden="true" />
              Acompanhar inscrição
            </Badge>
            <h1
              id="titulo-da-entrada"
              className="text-3xl font-bold leading-tight text-foreground lg:text-4xl lg:leading-10"
            >
              Acompanhar minha inscrição
            </h1>
            <p className="text-sm leading-relaxed text-muted-foreground sm:text-base sm:leading-6">
              Entre com o CPF e a senha que você criou na inscrição do Transporte Escolar Gratuito.
              Este acesso é só para acompanhar a inscrição — não é o Portal do Credenciado.
            </p>
          </div>

          <ul className="space-y-3">
            {VANTAGENS_DE_ENTRAR.map((vantagem) => (
              <li key={vantagem.texto} className="flex items-start gap-3 text-sm">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                  <vantagem.icone className="h-4 w-4" aria-hidden="true" />
                </span>
                <span className="pt-1.5 text-foreground">{vantagem.texto}</span>
              </li>
            ))}
          </ul>

          <div className="flex gap-2 rounded-lg border border-dashed border-border bg-muted/30 p-3 text-xs leading-relaxed text-muted-foreground">
            <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-success" aria-hidden="true" />
            <span>
              A sessão termina quando você fecha o navegador, ou depois de 30 minutos sem uso. Em
              computador público, saia e feche o navegador ao terminar.
            </span>
          </div>
        </section>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              {/* `h2` à mão: `CardTitle` renderiza `h3`, e aqui vem logo depois do `h1`. */}
              <h2 className="flex items-center gap-2 text-lg font-semibold leading-tight tracking-tight">
                <LogIn className="h-5 w-5 shrink-0 text-primary" aria-hidden="true" />
                Acesso do inscrito
              </h2>
              <CardDescription>Informe o CPF e a senha.</CardDescription>
            </CardHeader>
            <CardContent>
              {sessao.isPending ? (
                <div className="space-y-3" aria-busy="true" aria-label="Conferindo a sessão">
                  <Skeleton className="h-11 w-full" />
                  <Skeleton className="h-11 w-full" />
                  <Skeleton className="h-12 w-full" />
                </div>
              ) : sessao.data ? (
                <div className="space-y-4">
                  <Alert>
                    <ShieldCheck className="h-4 w-4" aria-hidden="true" />
                    <AlertTitle>Você já entrou</AlertTitle>
                    <AlertDescription>
                      {sessao.data.inscrito.nome ?? 'Inscrito'} · CPF{' '}
                      <span className="font-mono">{sessao.data.inscrito.cpfMascarado}</span>
                    </AlertDescription>
                  </Alert>
                  <Button asChild size="lg" className="h-12 w-full">
                    <Link to={DESTINO_DEPOIS_DE_ENTRAR}>
                      <ClipboardList className="h-4 w-4" aria-hidden="true" />
                      Ver minhas inscrições
                    </Link>
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    onClick={() => void sair()}
                  >
                    <LogOut className="h-4 w-4" aria-hidden="true" />
                    Sair
                  </Button>
                </div>
              ) : (
                <Form {...formulario}>
                  <form onSubmit={formulario.handleSubmit(entrar)} className="space-y-5" noValidate>
                    <FormField
                      control={formulario.control}
                      name="cpf"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>CPF</FormLabel>
                          <FormControl>
                            <Input
                              {...field}
                              onChange={(evento) => field.onChange(mascaraCpf(evento.target.value))}
                              placeholder="000.000.000-00"
                              maxLength={14}
                              inputMode="numeric"
                              autoComplete="username"
                              className="h-11 font-mono"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={formulario.control}
                      name="senha"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Senha</FormLabel>
                          <FormControl>
                            <CampoDeSenha
                              {...field}
                              autoComplete="current-password"
                              maxLength={256}
                              className="h-11"
                            />
                          </FormControl>
                          <FormDescription>
                            A senha que você criou ao se inscrever, ou ao redefinir.
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {recusa && (
                      <Alert variant="destructive" role="alert">
                        <AlertCircle className="h-4 w-4" aria-hidden="true" />
                        <AlertDescription>{recusa}</AlertDescription>
                      </Alert>
                    )}

                    <Button
                      type="submit"
                      size="lg"
                      className="h-12 w-full gap-2 text-sm font-semibold"
                      disabled={formulario.formState.isSubmitting}
                    >
                      {formulario.formState.isSubmitting ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                          Entrando…
                        </>
                      ) : (
                        <>
                          <LogIn className="h-4 w-4" aria-hidden="true" />
                          Entrar
                        </>
                      )}
                    </Button>

                    <div className="flex flex-wrap items-center justify-end gap-2 text-xs">
                      <Link
                        to="/credenciados/recuperar"
                        className="rounded text-link underline-offset-4 hover:underline focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
                      >
                        Esqueci minha senha
                      </Link>
                    </div>

                    <p className="border-t border-border pt-4 text-xs leading-relaxed text-muted-foreground">
                      Por segurança, a entrada com um CPF fica bloqueada por 1 hora depois de 5
                      tentativas sem sucesso. Se isso acontecer, espere o prazo ou redefina a senha.
                    </p>
                  </form>
                </Form>
              )}
            </CardContent>
          </Card>

          <div className="rounded-lg border border-dashed border-border bg-muted/30 p-4 text-center">
            <p className="mb-2 text-sm text-muted-foreground">Ainda não se inscreveu?</p>
            <Link
              to="/credenciados/autocadastro"
              className="inline-flex items-center gap-1.5 rounded text-sm font-medium text-link underline-offset-4 hover:underline focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
            >
              <UserPlus className="h-4 w-4" aria-hidden="true" />
              Fazer minha inscrição
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export default EntrarPage;
